import { groupHierarchically, WithChildren } from '@upright/common/model/input/blanket/blanket-product/hierarchy-utils';
import { IPRAttributes } from '@upright/common/model/input/companies/products/ipr';
import { PiuProductState } from '@upright/common/model/input/companies/products/product-input-ui/types';
import {
  ResolvedIpr,
  resolveIprAttributesRecursive,
} from '@upright/common/model/input/companies/products/propagate-ipr-attributes';
import { getMergedIpr } from '@upright/common/model/input/companies/products/product-input-ui/utils';

// A tree node carrying its own merged IPR (auto-detected + manual edits) — the input shape
// `resolveIprAttributesRecursive` needs to compute inheritance.
export type OwnIprNode = PiuProductState & { ownIpr: Partial<IPRAttributes> };

/** Annotate every node in the tree with its own merged IPR. */
export function attachOwnIpr(nodes: Array<WithChildren<PiuProductState>>): Array<WithChildren<OwnIprNode>> {
  return nodes.map(node => ({
    ...node,
    ownIpr: getMergedIpr({ ipr: node.ipr, manualIprEdits: node.manualIprEdits }),
    children: attachOwnIpr(node.children),
  }));
}

/**
 * Resolve IPR-attribute inheritance (PLT-5695) across a flat product set and index the resolved
 * blobs by product id. The Hierarchy view renders the resolved tree directly, but the flat List
 * view only has flat rows — it looks each row up here so ancestor-derived attributes show as
 * "(inherited)" in List mode too (PLT-5799).
 */
export function buildResolvedIprById(products: PiuProductState[]): Map<string, ResolvedIpr> {
  const resolved = resolveIprAttributesRecursive(attachOwnIpr(groupHierarchically(products)));
  const byId = new Map<string, ResolvedIpr>();
  const walk = (nodes: Array<WithChildren<OwnIprNode & ResolvedIpr>>): void => {
    for (const node of nodes) {
      byId.set(node.id, { inheritedIpr: node.inheritedIpr, resolvedIpr: node.resolvedIpr });
      walk(node.children);
    }
  };
  walk(resolved);
  return byId;
}
